import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHeader } from "../components/shared/PageHeader";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { submitInquiry } from "../lib/api";
import { SERVICES, ORG_TYPES, REGIONS, IMAGES } from "../data/content";

const TIMEFRAMES = ["Immediate (0-1 month)", "Near-term (1-3 months)", "This quarter", "Exploratory / Planning"];
const BUDGETS = ["Under $10k", "$10k - $50k", "$50k - $150k", "$150k+", "Not sure yet"];

export default function Quote() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    organization_type: "", region: "", budget: "", timeframe: "", message: "",
  });
  const [services, setServices] = useState([]);
  const [auditguard, setAuditguard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (title) =>
    setServices((s) => (s.includes(title) ? s.filter((t) => t !== title) : [...s, title]));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    if (services.length === 0) {
      toast.error("Please select at least one service of interest.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry({ type: "quote", ...form, services, auditguard_early_access: auditguard });
      toast.success("Quote request received. We'll respond shortly.");
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHeader
        testId="quote-header"
        eyebrow="Request a Quote"
        title="Scope the right engagement for your organization."
        subtitle="Tell us about your objectives and select the services you're interested in. We'll follow up with a tailored capability discussion."
        image={IMAGES.airport}
      />

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          {done ? (
            <Reveal>
              <div data-testid="quote-success" className="rounded-3xl border border-border bg-white p-10 text-center shadow-sm lg:p-16">
                <span className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Thank you — your request is in.</h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  A member of our team will review your request and reach out to schedule a
                  capability discussion. For urgent matters, please call us directly.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={onSubmit} data-testid="quote-form" className="rounded-3xl border border-border bg-white p-8 shadow-sm lg:p-10">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="q-name">Full name *</Label>
                    <Input id="q-name" data-testid="quote-name" value={form.name} onChange={update("name")} className="mt-2" placeholder="Jane Doe" />
                  </div>
                  <div>
                    <Label htmlFor="q-email">Email *</Label>
                    <Input id="q-email" type="email" data-testid="quote-email" value={form.email} onChange={update("email")} className="mt-2" placeholder="jane@airline.com" />
                  </div>
                  <div>
                    <Label htmlFor="q-company">Company / Organization</Label>
                    <Input id="q-company" data-testid="quote-company" value={form.company} onChange={update("company")} className="mt-2" placeholder="Your organization" />
                  </div>
                  <div>
                    <Label htmlFor="q-phone">Phone</Label>
                    <Input id="q-phone" data-testid="quote-phone" value={form.phone} onChange={update("phone")} className="mt-2" placeholder="+1 000 000 0000" />
                  </div>
                  <div>
                    <Label>Organization type</Label>
                    <Select value={form.organization_type} onValueChange={setField("organization_type")}>
                      <SelectTrigger data-testid="quote-org-type" className="mt-2"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Region</Label>
                    <Select value={form.region} onValueChange={setField("region")}>
                      <SelectTrigger data-testid="quote-region" className="mt-2"><SelectValue placeholder="Select region" /></SelectTrigger>
                      <SelectContent>
                        {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-8">
                  <Label className="text-base">Services of interest *</Label>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {SERVICES.map((s) => {
                      const active = services.includes(s.title);
                      return (
                        <label
                          key={s.slug}
                          data-testid={`quote-service-${s.slug}`}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${active ? "border-brand bg-brand/5" : "border-border hover:bg-secondary/60"}`}
                        >
                          <Checkbox checked={active} onCheckedChange={() => toggleService(s.title)} className="mt-0.5" />
                          <span className="text-sm font-medium text-navy">{s.title}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label>Budget range</Label>
                    <Select value={form.budget} onValueChange={setField("budget")}>
                      <SelectTrigger data-testid="quote-budget" className="mt-2"><SelectValue placeholder="Select budget" /></SelectTrigger>
                      <SelectContent>
                        {BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Timeframe</Label>
                    <Select value={form.timeframe} onValueChange={setField("timeframe")}>
                      <SelectTrigger data-testid="quote-timeframe" className="mt-2"><SelectValue placeholder="Select timeframe" /></SelectTrigger>
                      <SelectContent>
                        {TIMEFRAMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-6">
                  <Label htmlFor="q-message">Project details</Label>
                  <Textarea id="q-message" data-testid="quote-message" value={form.message} onChange={update("message")} className="mt-2 min-h-[130px]" placeholder="Describe your goals, context and any specific requirements." />
                </div>

                <label data-testid="quote-auditguard" className={`mt-6 flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${auditguard ? "border-aero bg-aero/5" : "border-border hover:bg-secondary/60"}`}>
                  <Checkbox checked={auditguard} onCheckedChange={() => setAuditguard((v) => !v)} className="mt-0.5" />
                  <span className="text-sm font-medium text-navy">
                    I'm interested in <span className="font-semibold text-aero">AuditGuard AI</span> early access
                    <span className="mt-0.5 block text-xs font-normal text-muted-foreground">Be first to know when our flagship compliance platform launches.</span>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit"
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-aero px-8 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                  {loading ? "Submitting..." : "Submit quote request"}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
